'use client';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from 'components/ui/button';
import { Accordion } from 'components/ui/accordion';

import { EducationNode, QualificationNode, UniversityNode } from 'generated/types';
import { useDeleteUserEducationMutation } from 'generated/hooks';
import { UpdateEducationMutation, UserQuery } from 'generated/operations';
import { useToast } from 'hooks/useToast';
import { months } from 'utils/date';

import { TabIds } from '../ProfileForm';
import { EducationCard, EducationNodeFromBe, IEducationForm } from './EducationCard';

type EditUserNode = NonNullable<UserQuery['user']>;

interface IEducation {
  qualifications: QualificationNode[];
  universities: UniversityNode[];
  currentTab: TabIds;
  user: EditUserNode;
  onSetDirtyForm: (formName: TabIds, isDirty: boolean, userData?: EditUserNode) => void;
  onUpdateUser: (user: EditUserNode) => void;
}

export const Education: FC<IEducation> = (props) => {
  const {
    universities,
    qualifications,
    currentTab,
    user,
    onSetDirtyForm,
    onUpdateUser
  } = props;

  const [, deleteUserEducation] = useDeleteUserEducationMutation();

  const education = useMemo(() => user.education ?? [], [user.education]);

  const [savedEducationFromBe, setSavedEducationFromBe] = useState<EducationNode[]>(education ?? []);

  const { handleToast } = useToast();

  const [accordionValues, setValues] = useState<string[]>(education.length ? ['1'] : []);

  const [savedEducation, setEducation] = useState<{ education: (IEducationForm & { localId?: number })[] }>({
    education: savedEducationFromBe.map(educationItem => ({
      id: educationItem.id,
      university: {
        id: educationItem.university?.id,
        name: educationItem.university?.name
      } as UniversityNode,
      qualification: {
        id: educationItem.qualification?.id ?? '',
        name: educationItem.qualification?.name ?? ''
      },
      fromMonth: months.find(month => month.value === (educationItem.fromMonth ? educationItem.fromMonth - 1 : null)) || null,
      fromYear: educationItem.fromYear ? {
        value: educationItem.fromYear,
        title: educationItem.fromYear.toString()
      } : { value: 0, title: '' },
      toMonth: months.find(month => month.value === (educationItem.toMonth ? educationItem.toMonth - 1 : null)) ?? null,
      toYear: educationItem.toYear ? {
        value: educationItem.toYear,
        title: educationItem.toYear.toString()
      } : null,
      description: '',
      isCurrentlyStudyHere: !educationItem.toMonth && !educationItem.toYear
    })) ?? []
  });

  const [dirtyCards, setDirtyCards] = useState<Record<string, boolean>>(education.reduce((acc: Record<string, boolean>, item) => {
    acc[item.id] = false;
    return acc;
  }, {}) ?? {});

  const handleUpdate = useCallback((education: IEducationForm & {
      localId?: number
    }, educationFromBE: EducationNodeFromBe) => {
      setEducation(prevState => {
        const newValue = prevState.education.map(item => {
          if (item.id === education.id || (!!item.localId && !!education.localId && item.localId === education.localId)) {
            return ({
              ...item,
              ...education
            });
          }

          return item;
        });

        return ({
          education: [...newValue]
        });
      });

      if (educationFromBE) {
        setSavedEducationFromBe(prevState => {
          const index = prevState.findIndex(item => item.id === educationFromBE.id);
          if (index === -1) {
            return [...prevState, educationFromBE];
          }
          return prevState.map(item =>
            item.id === educationFromBE.id ? { ...item, ...educationFromBE } : item
          );
        });
      }
    },
    []
  );

  const handleDelete = useCallback(async (education: IEducationForm & { localId?: number }) => {
    if (!education.id && education.localId) {
      setEducation(prevState => {
        const newArray = [...prevState.education.filter((item) => item?.localId !== education.localId)];

        setDirtyCards(prevState => {
          const newState = { ...prevState };

          if (education.localId) {
            delete newState[education.localId.toString()];
          }

          if (education.id) {
            delete newState[education.id.toString()];
          }

          return { ...newState };
        });

        return ({
          education: [...newArray]
        });
      });
    } else {
      const response = await deleteUserEducation({
        id: education.id ? +education.id : 0
      });

      if (response.error) {
        handleToast(response.error.message, 'error');
        console.error(response.error);
        return;
      }

      setEducation(prevState => {
        const newValues = prevState.education.filter(item => item.id !== education.id);

        return {
          education: newValues
        };
      });

      const updatedUser: EditUserNode = {
        ...user,
        education:
          savedEducation.education
            .filter(item => item.id !== education.id)
            .map(({ localId, ...item }) => ({
              ...item,
              id: item.id ?? '',
              fromYear: item.fromYear?.value ?? 0,
              toYear: item.toYear?.value ?? 0,
              fromMonth: item.fromMonth?.value ?? 0,
              toMonth: item.toMonth?.value ?? 0
            }))
      };

      onUpdateUser(updatedUser);
      setSavedEducationFromBe(prevState => prevState.filter(item => item.id !== education.id))
    }

    handleToast('Education successfully deleted!', 'success');
  }, [deleteUserEducation, handleToast, onUpdateUser, savedEducation.education, user]);

  const isDirty = useMemo(() => {
    return Object.values(dirtyCards).some(item => item);
  }, [dirtyCards]);

  const handleCardDirty = useCallback((id: string, isDirty: boolean) => {
    setDirtyCards(prevState => {
      return {
        ...prevState,
        [id]: isDirty
      };
    });
  }, []);

  useEffect(() => {
    onSetDirtyForm(TabIds.EDUCATION, isDirty);
  }, [isDirty, onSetDirtyForm]);

  useEffect(() => {
    if (currentTab !== TabIds.EDUCATION) {
      onSetDirtyForm(TabIds.EDUCATION, false, {
        ...user,
        education: savedEducationFromBe
      });
    }
  }, [currentTab, onSetDirtyForm, savedEducationFromBe, user]);

  return (
    <div className="flex flex-col gap-10">
      <Accordion type="multiple" value={accordionValues} onValueChange={(value) => setValues(() => [...value])}>
        {savedEducation.education.map((education, index, array) => (
          <EducationCard
            isOpen={accordionValues.includes((index + 1).toString())}
            key={education.id || education.localId || index}
            qualifications={qualifications}
            universities={universities}
            educationItem={education}
            index={index}
            isLastCard={array.length - 1 === index}
            onUpdateItem={handleUpdate}
            onDelete={handleDelete}
            onSetDirtyCard={handleCardDirty}
          />
        ))}
      </Accordion>
      <Button
        variant="nakedGrayLink"
        onClick={() => {
          const localId = new Date().getTime();

          setEducation(prevState => {
            const newValue = {
              education: [...prevState.education, {
                university: {id: '', name: ''},
                qualification: {
                  id: '',
                  name: '',
                },
                description: '',
                fromYear: {
                  value: new Date().getFullYear(),
                  title: new Date().getFullYear().toString()
                },
                id: '',
                toMonth: null,
                toYear: null,
                fromMonth: null,
                isCurrentlyStudyHere: false,
                localId
              }]
            };

            handleCardDirty(localId.toString(), true);

            setValues(prevState => [...new Set([...prevState, newValue.education.length.toString()])]);

            return newValue;
          });
        }}
      >
        Add new education +
      </Button>
    </div>
  );
};
